import React, { memo, useContext } from 'react';
import isEqual from 'lodash.isequal';
import { Box } from '@hz-design-system/web-ui';
import { imageFormatter } from '@/client/utils/formatters';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import { Attribute } from '@/client/types/Listings';
import { TImage } from '@/client/types/Images';

import ListingVipLink from '@/client/components/Listings/atoms/ListingVipLink';
import {
  ListingAttributes,
  ListingPriceDateFeature,
  ListingSellerInfo,
  ListingTitleDescription,
  ListingTrustIndicators,
} from '@/client/components/ListingsNew/ListingListViewContentHorizontal/ListingListViewDesktop/components';
import { ListingFigure } from '@/client/components/ListingsNew/components';

import Classes from './ListingListViewContentDesktop.module.scss';
import experiments from '@/utils/labs/experiments';

type TListingListViewContentDesktop = {
  carPassUrl?: Attribute;
  categories?: TCategories;
  correlationId?: string;
  date: string;
  decodingType?: 'sync' | 'async' | 'auto';
  description: string;
  energyLabel?: Attribute;
  extendedAttributes?: Attribute[];
  fetchPriority?: 'auto' | 'high' | 'low';
  isBottomBlock?: boolean;
  isFavorite?: boolean;
  isVerifiedSeller: boolean;
  loadingType?: 'eager' | 'lazy';
  location: { label: string; distance?: string };
  mileage?: Attribute;
  napAvailable?: boolean;
  onFavorite: () => void;
  opvalStickerText?: string;
  pageLocation?: string;
  pictures?: TImage[];
  price: string;
  priorityProduct?: string;
  regularAttributes?: Attribute[];
  sellerInformation?: TSellerInformation;
  thinContent?: boolean;
  title: string;
  trackingData?: string;
  trustIndicators?: { value?: string; key: string }[];
  urgencyFeatureActive?: boolean;
  vipUrl: string;
};

const ListingListViewContentDesktop = ({
  carPassUrl,
  categories,
  correlationId,
  date,
  decodingType,
  description,
  energyLabel,
  extendedAttributes = [],
  fetchPriority,
  isBottomBlock = false,
  isFavorite = false,
  isVerifiedSeller,
  loadingType,
  location,
  mileage,
  napAvailable = false,
  onFavorite,
  opvalStickerText,
  pageLocation,
  pictures,
  price,
  priorityProduct = '',
  regularAttributes = [],
  sellerInformation,
  thinContent,
  title,
  trackingData,
  trustIndicators = [],
  urgencyFeatureActive = false,
  vipUrl,
}: TListingListViewContentDesktop) => {
  const { tenantContext, labsConfig } = useContext(EnvironmentContext);
  const { isListViewFavoriteButtonEnabled } = experiments({ labsConfig });

  return (
    <>
      <ListingVipLink
        vipUrl={vipUrl}
        trackingData={trackingData}
        correlationId={correlationId}
        pageLocation={pageLocation}
        thinContent={thinContent}
        className={Classes['hz-Listing-coverLink']}
      >
        <ListingFigure
          images={imageFormatter(pictures ?? [], title, categories, extendedAttributes)}
          opvalStickerText={opvalStickerText}
          fetchPriority={fetchPriority}
          loadingType={loadingType}
          decodingType={decodingType}
          isFavorite={isFavorite}
          onFavorite={onFavorite}
          urgencyFeatureActive={urgencyFeatureActive}
          {...(isListViewFavoriteButtonEnabled && { showFavoriteIcon: true })}
        />

        <Box as="div" name="Listing-group--desktop" className={Classes['hz-Listing-group']}>
          <Box
            as="div"
            name="Listing-group--title-description-attributes"
            className={Classes['hz-Listing-group--title-description-attributes']}
          >
            <ListingTitleDescription title={title} description={description} />
            <ListingTrustIndicators trustIndicators={trustIndicators} />
            <ListingAttributes
              regularAttributes={regularAttributes}
              mileage={mileage}
              carPassUrl={carPassUrl}
              napAvailable={napAvailable}
              energyLabel={energyLabel}
              tenantContext={tenantContext}
            />
          </Box>
          <ListingPriceDateFeature price={price} date={date} priorityProduct={priorityProduct} />
        </Box>
      </ListingVipLink>

      <ListingSellerInfo
        sellerInformation={sellerInformation}
        isVerifiedSeller={isVerifiedSeller}
        location={location}
        isBottomBlock={isBottomBlock}
      />
    </>
  );
};

// todo: find which properties are really needs to be checked for preventing rerendering
function arePropsEqual(prevProps, nextProps) {
  return isEqual(prevProps, nextProps);
}

export default memo(ListingListViewContentDesktop, arePropsEqual);
